import { Application } from "./application";
import { C } from "../common/component";
import { Dom } from "../common/dom";
import { Txt } from "../common/text";
import { Helper } from "../common/helper";
import { Icon, IconHelper } from "../common/icon-helper";
import { Notifications } from "../common/notifications";
import { AuthenticationHandler } from "../common/authentication-handler";
import { TimerService } from "../timer/timer-service";
import { TimerButton } from "../timer/timer-button";
import { MenuBuilder } from "./menu-builder";
import { PageInfo, PageNavigator } from "./page-navigator";
import { SearchBox } from "./search-box";
import { DomainHelper } from "../common/domain-helper";
import { Pwa } from "./pwa";
import { NavigationHelper } from "./navigation-helper";

interface DropdownItem {
    link: HTMLElement;
    menu: HTMLElement;
}

interface NavbarItem {
    left: HTMLElement;
    right: HTMLElement;
}

// ReSharper disable InconsistentNaming
export interface NavigationItem {
    Name: string;
    DisplayName: string;
    Icon: string;
    Pages: string[];
}

export interface NavigationBarItem {
    DisplayName: string;
    Icon: string;
    Children: NavigationItem[];
}
// ReSharper restore InconsistentNaming

export class Navbar
{
    private readonly data: NavigationBarItem[];
    private readonly hasTimer: boolean;
    private readonly timerPage: string;
    private readonly pages: PageInfo[];
    private readonly profileData;
    private readonly notificationsPage: string;

    constructor(data: NavigationBarItem[], hasTimer: boolean, timerPage: string, pages: PageInfo[], profileData,
                notificationsPage: string)
    {
        this.data = data;
        this.hasTimer = hasTimer;
        this.timerPage = timerPage;
        this.pages = pages;
        this.profileData = profileData;
        this.notificationsPage = notificationsPage;
    }

    appendTo(parent)
    {
        const mainBody = Dom.createDiv();
        parent.append(mainBody);

        const navElement = Dom.add("nav", mainBody, "w-navbar");
        const container = Dom.add("div", navElement, "w-navbar-container");
        const { left, right } = this.createNavbar(container);

        Application.mainElement.appendTo(mainBody);
        Application.secondaryElement.appendTo(mainBody);

        this.createLeftMenu(left);
        this.createRightMenu(right);
    }

    private createLeftMenu(parent: HTMLElement): void
    {
        for (const item of this.data)
        {
            const { link, menu } = this.createDropdown(parent);
            IconHelper.addIconWithText(link, Icon[item.Icon], item.DisplayName);
            const increment = 100 / item.Children.length;
            let percentage = 0;
            for (const child of item.Children)
            {
                MenuBuilder.createMenuElement(child, menu, percentage);
                percentage += increment;
            }
        }
    }

    private async createRightMenu(parent: HTMLElement): Promise<void>
    {
        new SearchBox(this.pages).appendTo(parent);
        const dropdown = this.createDropdown(parent);

        dropdown.link.classList.add("w-user-menu");
        const photo = Dom.add("img", dropdown.link);
        photo.src = this.profileData.photo || "images/profile.png";
        const nameElement = Dom.add("span", dropdown.link);
        nameElement.textContent = this.profileData.name || Txt.nameless;
        let redDot: HTMLElement | undefined;
        if (this.profileData.hasRedDot)
        {
            redDot = Dom.add("i", nameElement);
            redDot.style.backgroundColor = "red";
            redDot.style.position = "absolute";
            redDot.style.top = "15px";
            redDot.style.right = "8px";
            redDot.style.width = "8px";
            redDot.style.height = "8px";
            redDot.style.borderRadius = "100%";
        }
        for (const pageName of this.profileData.pages)
        {
            this.createMenuItem(dropdown.menu, PageNavigator.getDisplayName(pageName), PageNavigator.getIcon(pageName), () =>
            {
                if (this.profileData.redDotPage === pageName && redDot)
                {
                    redDot.style.display = "none";
                }
                PageNavigator.navigateToPage(pageName);
            });
        }
        if (!Helper.isMedian() && Helper.isMobile() && !Helper.isPWA())
        {
            this.createMenuItem(dropdown.menu, Txt.downloadApp, Icon.download, Pwa.downloadApp);
        }
        this.createMenuItem(dropdown.menu, Txt.logout, Icon.off, async () =>
        {
            await AuthenticationHandler.logout();
            if (!Helper.isMedian())
            {
                Helper.refreshPage();
            }
        });

        Notifications.initialize(parent, this.notificationsPage);

        if (this.hasTimer)
        {
            // TimerButton consumes a jQuery element, so this one boundary stays jQuery.
            TimerService.button = new TimerButton(C.li.appendTo(parent));
            TimerService.timerPageName = this.timerPage;
            TimerService.updateCurrentState();
        }
    }

    private createNavbar(parent: HTMLElement): NavbarItem
    {
        const header = Dom.add("div", parent, "w-navbar-header");
        const navbarElement = Dom.add("div", parent, "w-navbar-collapse");
        navbarElement.id = "navbar";

        const menuButton = Dom.add("button", header, "w-navbar-toggle");
        menuButton.type = "button";
        menuButton.setAttribute("aria-expanded", "false");
        menuButton.setAttribute("aria-controls", "navbar");
        NavigationHelper.registerCollapseToggle(menuButton, navbarElement);
        Dom.add("span", menuButton, "w-sr-only").textContent = "Menu";
        Dom.add("span", menuButton, "w-navbar-bar");
        Dom.add("span", menuButton, "w-navbar-bar");
        Dom.add("span", menuButton, "w-navbar-bar");

        const domain = DomainHelper.getDomain();
        const brandLink = Dom.add("a", header, "w-navbar-brand");
        brandLink.style.marginTop = "-15px";
        brandLink.addEventListener("click", () => PageNavigator.navigateToHome());
        const logo = Dom.add("img", brandLink);
        logo.src = domain === "rbe" ? "/images/rbelogo.svg" : "/images/logo.png";
        logo.alt = "Wemply_official_logo";
        logo.setAttribute("height", "50px");

        if (domain !== "rbe")
        {
            const companyLink = Dom.add("a", header, "w-navbar-brand", "w-navbar-companyname");
            companyLink.textContent = "Wemply";
            companyLink.addEventListener("click", () => PageNavigator.navigateToHome());
            const small = Dom.add("small", companyLink);
            small.style.display = "block";
            small.style.fontSize = "50%";
            small.style.textAlign = "right";
            small.textContent = domain;
        }

        return {
            left: Dom.add("ul", navbarElement, "w-nav"),
            right: Dom.add("ul", navbarElement, "w-nav", "w-nav-right")
        };
    }

    private createDropdown(parent: HTMLElement): DropdownItem
    {
        const li = Dom.add("li", parent, "w-dropdown");
        const link = Dom.add("a", li, "w-dropdown-toggle");
        link.setAttribute("role", "button");
        link.setAttribute("aria-haspopup", "true");
        link.setAttribute("aria-expanded", "false");
        NavigationHelper.registerDropdown(li, link);
        return {
            link,
            menu: Dom.add("ul", li, "w-dropdown-menu")
        };
    }

    private createMenuItem(parent: HTMLElement, text: string, icon: Icon, callback): void
    {
        const item = Dom.add("li", parent);
        const link = Dom.add("a", item);
        IconHelper.addIconWithText(link, icon, text);

        link.addEventListener("click", () =>
        {
            NavigationHelper.dismiss();
            callback();
        });
    }
}
